import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Tooltip,
  Menu,
  MenuItem,
  Fab,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Card,
  CardContent,
  Divider,
  ListItemIcon,
  ListItemText,
  Badge,
  Collapse,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  FileDownload as ExportIcon,
  LocalOffer as PriceIcon,
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  Sort as SortIcon,
  LocationOn as LocationIcon,
  Check as CheckIcon,
  FilterAlt as FilterAltIcon,
  Warning as WarningIcon,
  ReportProblem as IssueIcon,
  Keyboard as KeyboardIcon,
  Memory as MemoryIcon,
  BatteryChargingFull as BatteryIcon,
  Wifi as WifiIcon,
  Speaker as SpeakerIcon,
  Whatshot as WhatshotIcon,
  BugReport as BugReportIcon,
  Build as BuildIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocalShipping as GrnIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Update as UpdateIcon,
  ConfirmationNumber as SerialIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getProducts, deleteProduct, getProductIssues, getAllProductIssues } from '../../services/api';
import GRNProductHistory from '../../components/GRNProductHistory';
import { canEditProducts, canDeleteProducts } from '../../utils/auth';

const dedupeIssues = (issues) => {
  if (!Array.isArray(issues)) return [];
  const seen = new Set();
  return issues.filter(issue => {
    if (['pending', 'in_progress'].includes(issue.status)) {
      const key = `${(issue.serial_number || '').trim().toLowerCase()}|${issue.status}`;
      if (seen.has(key)) return false;
      seen.add(key);
    }
    return true;
  });
};

const computeIssuesStats = (issuesByProduct) => {
  const allIssues = dedupeIssues(Object.values(issuesByProduct).flat());
  return {
    totalIssues: allIssues.filter(i =>
      ['pending', 'in_progress', 'cannot_fix'].includes(i.status)
    ).length,
    pendingIssues: allIssues.filter(i => i.status === 'pending').length,
    inProgressIssues: allIssues.filter(i => i.status === 'in_progress').length,
    resolvedIssues: allIssues.filter(i => i.status === 'resolved').length
  };
};

const Products = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  
  // State for issues
  const [productIssues, setProductIssues] = useState({});
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [loadingIssues, setLoadingIssues] = useState({});
  const [issuesDialogOpen, setIssuesDialogOpen] = useState(false);
  const [selectedProductForIssues, setSelectedProductForIssues] = useState(null);
  const [issuesStats, setIssuesStats] = useState({
    totalIssues: 0,
    pendingIssues: 0,
    inProgressIssues: 0,
    resolvedIssues: 0
  });
  const [refreshingIssues, setRefreshingIssues] = useState(false);
  const [grnHistoryOpen, setGrnHistoryOpen] = useState(false);
  const [selectedProductForGrn, setSelectedProductForGrn] = useState(null);
  const [allIssuesDialogOpen, setAllIssuesDialogOpen] = useState(false);
  const [allIssuesList, setAllIssuesList] = useState([]);
  const [loadingAllIssues, setLoadingAllIssues] = useState(false);
  const [allIssuesSearch, setAllIssuesSearch] = useState('');
  const [allIssuesStatusFilter, setAllIssuesStatusFilter] = useState('all');

  // Load products once — search filters client-side (no page remount on each keystroke)
  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = { limit: 500 };
      const response = await getProducts(params);
      if (response.success) {
        setProducts(response.data);
        setCategories(response.categories || []);
        
        // Extract unique locations from products
        const locationSet = new Set();
        response.data.forEach(product => {
          if (product.location && product.location.trim() !== '') {
            locationSet.add(product.location);
          }
        });
        
        const uniqueLocations = Array.from(locationSet).sort();
        setLocations(uniqueLocations);

        // Fetch issues for products that have issue_quantity > 0
        const productsWithIssues = response.data.filter(p => (p.issue_quantity || 0) > 0);
        if (productsWithIssues.length > 0) {
          await fetchAllIssues(productsWithIssues.map(p => p.id));
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load products');
      console.error('Products error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all issues for multiple products
  const fetchAllIssues = async (productIds) => {
    try {
      setRefreshingIssues(true);
      const collected = {};

      for (const productId of productIds) {
        collected[productId] = await fetchProductIssues(productId, { skipStats: true });
      }

      setProductIssues(prev => {
        const next = { ...prev, ...collected };
        setIssuesStats(computeIssuesStats(next));
        return next;
      });

      // Sync product issue_quantity after backend duplicate cleanup
      const refreshed = await getProducts();
      if (refreshed.success) {
        setProducts(refreshed.data);
      }
    } catch (err) {
      console.error('Error fetching issues:', err);
    } finally {
      setRefreshingIssues(false);
    }
  };

  // Fetch issues for a specific product
  const fetchProductIssues = async (productId, options = {}) => {
    const { skipStats = false } = options;
    try {
      setLoadingIssues(prev => ({ ...prev, [productId]: true }));

      const response = await getProductIssues(productId);

      if (response && response.success) {
        let issues = [];
        if (response.data && response.data.issues) {
          issues = dedupeIssues(response.data.issues);
        } else if (Array.isArray(response.data)) {
          issues = dedupeIssues(response.data);
        } else if (Array.isArray(response)) {
          issues = dedupeIssues(response);
        }

        if (!skipStats) {
          setProductIssues(prev => {
            const next = { ...prev, [productId]: issues };
            setIssuesStats(computeIssuesStats(next));
            return next;
          });
        }

        return issues;
      }
      return [];
    } catch (err) {
      console.error(`Error fetching issues for product ${productId}:`, err);
      if (!skipStats) {
        setProductIssues(prev => {
          const next = { ...prev, [productId]: [] };
          setIssuesStats(computeIssuesStats(next));
          return next;
        });
      }
      return [];
    } finally {
      setLoadingIssues(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Refresh issues for a specific product
  const refreshProductIssues = (productId) => {
    fetchProductIssues(productId);
  };

  const fetchAllIssuesList = async () => {
    try {
      setLoadingAllIssues(true);
      const response = await getAllProductIssues({ limit: 1000 });
      let issues = response?.data || [];
      if (!Array.isArray(issues)) issues = [];
      setAllIssuesList(dedupeIssues(issues));
    } catch (err) {
      console.error('Error fetching all issues:', err);
      setAllIssuesList([]);
    } finally {
      setLoadingAllIssues(false);
    }
  };

  const handleViewAllIssues = () => {
    setAllIssuesDialogOpen(true);
    setAllIssuesSearch('');
    setAllIssuesStatusFilter('all');
    fetchAllIssuesList();
  };

  const filteredAllIssues = allIssuesList.filter(issue => {
    if (allIssuesStatusFilter !== 'all' && issue.status !== allIssuesStatusFilter) return false;
    if (!allIssuesSearch.trim()) return true;
    const term = allIssuesSearch.toLowerCase();
    return (
      (issue.serial_number || '').toLowerCase().includes(term) ||
      (issue.issue_type || '').toLowerCase().includes(term) ||
      (issue.issue_description || '').toLowerCase().includes(term) ||
      (issue.product?.name || '').toLowerCase().includes(term) ||
      (issue.product?.product_code || '').toLowerCase().includes(term)
    );
  });

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteProduct(id);
        fetchProducts(); // Refresh the list
      } catch (err) {
        setError(err.message || 'Failed to delete product');
      }
    }
  };

  const handleViewGrnHistory = (product) => {
    setSelectedProductForGrn(product);
    setGrnHistoryOpen(true);
  };

  const handleView = (product) => {
    setSelectedProduct(product);
    setOpenDialog(true);
  };

  const handleAddProduct = () => {
    navigate('/products/new');
  };

  const handleEditProduct = (id) => {
    navigate(`/products/edit/${id}`);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleCategoryFilterChange = (category) => {
    setCategoryFilter(category);
    handleFilterClose();
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    handleFilterClose();
  };

  const handleLocationFilterChange = (location) => {
    setLocationFilter(location);
    handleFilterClose();
  };

  // Clear all filters
  const handleClearFilters = () => {
    setCategoryFilter('all');
    setStatusFilter('all');
    setLocationFilter('all');
    setSearchQuery('');
    handleFilterClose();
  };

  // Handle view all issues for a product
  const handleViewIssues = (product) => {
    setSelectedProductForIssues(product);
    setIssuesDialogOpen(true);
    // Refresh issues when opening dialog
    if (product.issue_quantity > 0) {
      refreshProductIssues(product.id);
    }
  };

  // Handle expand/collapse product to show issues
  const handleExpandProduct = (productId) => {
    if (expandedProduct === productId) {
      setExpandedProduct(null);
    } else {
      setExpandedProduct(productId);
      // Fetch issues if not already loaded or if count doesn't match
      const product = products.find(p => p.id === productId);
      if (product && product.issue_quantity > 0) {
        const currentIssues = productIssues[productId] || [];
        if (currentIssues.length !== product.issue_quantity) {
          fetchProductIssues(productId);
        }
      }
    }
  };

  // Helper function to format date for CSV
  const formatDateForCSV = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Helper function to format currency for CSV (remove symbols)
  const formatCurrencyForCSV = (amount) => {
    if (!amount && amount !== 0) return '0.00';
    if (typeof amount === 'string') {
      // Remove currency symbols and commas
      return amount.replace(/[^0-9.-]/g, '');
    }
    return parseFloat(amount || 0).toFixed(2);
  };

  // Calculate inventory value
  const calculateInventoryValue = (product) => {
    const price = parseFloat(product.price) || 0;
    const quantity = parseInt(product.quantity) || 0;
    return price * quantity;
  };

  // Export to CSV function
  const handleExportCSV = () => {
    if (filteredProducts.length === 0) {
      alert('No products to export');
      return;
    }

    // Define CSV headers
    const headers = [
      'Product Code',
      'Product Name',
      'Category',
      'Description',
      'Unit',
      'Price (LKR)',
      'Cost Price (LKR)',
      'Total Quantity',
      'Good Quantity',
      'Issue Quantity',
      'Inventory Value (LKR)',
      'Reorder Level',
      'Min Stock',
      'Barcode',
      'Supplier',
      'Location',
      'Warranty Months',
      'Warranty Type',
      'Is Low Stock',
      'Status',
      'Created Date',
      'Updated Date'
    ];

    // Prepare CSV data
    const csvData = filteredProducts.map(product => {
      // Calculate inventory value
      const inventoryValue = calculateInventoryValue(product);
      const totalQty = product.quantity || 0;
      const goodQty = product.good_quantity || 0;
      const issueQty = product.issue_quantity || 0;
      
      return [
        product.product_code || '',
        product.name || '',
        product.category || '',
        product.description || '',
        product.unit || 'pcs',
        formatCurrencyForCSV(product.price),
        formatCurrencyForCSV(product.cost_price),
        totalQty,
        goodQty,
        issueQty,
        inventoryValue.toFixed(2),
        product.reorder_level || '',
        product.min_quantity || '',
        product.barcode || '',
        product.supplier || '',
        product.location || '',
        product.warranty_months || '',
        product.warranty_type || '',
        product.is_low_stock ? 'Yes' : 'No',
        product.is_active ? 'Active' : 'Inactive',
        formatDateForCSV(product.created_at),
        formatDateForCSV(product.updated_at)
      ];
    });

    // Add summary rows
    const totalGood = filteredProducts.reduce((sum, p) => sum + (p.good_quantity || 0), 0);
    const totalIssue = filteredProducts.reduce((sum, p) => sum + (p.issue_quantity || 0), 0);
    const totalValue = filteredProducts.reduce((sum, p) => sum + calculateInventoryValue(p), 0);

    const summaryData = [
      [], // Empty row
      ['SUMMARY', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      ['Total Products:', filteredProducts.length, '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      ['Total Good Items:', totalGood, '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      ['Total Issue Items:', totalIssue, '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      ['Total Inventory Value:', '', '', '', '', '', '', '', '', totalValue.toFixed(2), '', '', '', '', '', '', '', '', '', '', ''],
      ['Low Stock Items:', getLowStockCount(), '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      ['Active Products:', getActiveCount(), '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      ['Export Date:', new Date().toLocaleDateString(), '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '']
    ];

    // Combine all data
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(',')),
      ...summaryData.map(row => row.join(','))
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `products_export_${new Date().toISOString().split('T')[0].replace(/-/g, '')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Export issues to CSV
  const handleExportIssues = () => {
    window.open('/api/products/export/issues', '_blank');
  };

  // Function to get category color
  const getCategoryColor = (category) => {
    const colorMap = {
      'Desktop Computers': '#1565c0',
      'Laptop Computers': '#1e88e5',
      'All-in-One Computers': '#2196f3',
      'Workstation PCs': '#0d47a1',
      'Mini PCs': '#1976d2',
      'Processors (CPU)': '#7b1fa2',
      'Motherboards': '#9c27b0',
      'Graphics Cards (GPU)': '#ba68c8',
      'Memory (RAM)': '#8e24aa',
      'Power Supplies (PSU)': '#6a1b9a',
      'Computer Cases': '#4a148c',
      'Cooling Solutions': '#ab47bc',
      'Charger': '#9c30af',
      'Internal SSDs': '#2e7d32',
      'Internal HDDs': '#388e3c',
      'External Hard Drives': '#43a047',
      'USB Flash Drives': '#4caf50',
      'Memory Cards': '#66bb6a',
      'Keyboards': '#ef6c00',
      'Computer Mice': '#f57c00',
      'Webcams': '#ff9800',
      'Speakers': '#ffb74d',
      'Headsets & Microphones': '#ffa726',
      'Computer Monitors': '#c2185b',
      'Monitor Mounts': '#d81b60',
      'Projectors': '#e91e63',
      'Routers': '#006064',
      'Switches': '#00838f',
      'Network Adapters': '#0097a7',
      'Cables & Connectors': '#00acc1',
      'Wi-Fi Extenders': '#00bcd4',
      'Gaming PCs': '#f57c00',
      'Gaming Laptops': '#ff9800',
      'Gaming Accessories': '#ffb74d',
      'VR Headsets': '#ffcc80',
      'Gaming Consoles': '#ffa726',
      'Operating Systems': '#33691e',
      'Productivity Software': '#558b2f',
      'Security Software': '#689f38',
      'Creative Software': '#7cb342',
      'Utilities': '#8bc34a',
      'Laptop Bags & Cases': '#283593',
      'Mouse Pads': '#3949ab',
      'Cable Management': '#3f51b5',
      'Cleaning Supplies': '#5c6bc0',
      'UPS & Surge Protectors': '#7986cb',
      'Printers': '#f9a825',
      'Scanners': '#fbc02d',
      'Ink & Toner': '#ffd54f',
      'Replacement Parts': '#004d40',
      'Tools & Kits': '#00695c',
      'Thermal Paste & Pads': '#00796b'
    };
    
    if (colorMap[category]) {
      return colorMap[category];
    }
    
    const colors = [
      '#e3f2fd', '#f3e5f5', '#e8f5e9', '#fff3e0', '#fce4ec',
      '#e0f7fa', '#fff8e1', '#f1f8e9', '#e8eaf6', '#fffde7',
      '#e0f2f1', '#f9fbe7', '#ffebee', '#f3e5f5', '#e8f5e9'
    ];
    
    let hash = 0;
    for (let i = 0; i < category.length; i++) {
      hash = category.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };

  // Function to get category text color (for contrast)
  const getCategoryTextColor = (category) => {
    const bgColor = getCategoryColor(category);
    const r = parseInt(bgColor.slice(1, 3), 16);
    const g = parseInt(bgColor.slice(3, 5), 16);
    const b = parseInt(bgColor.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  // Get issue type icon
  const getIssueTypeIcon = (issueType) => {
  const iconMap = {
        'keyboard_issue': <KeyboardIcon fontSize="small" color="secondary" />,
    'motherboard_issue': <MemoryIcon fontSize="small" color="primary" />,
    'screen_issue': <CategoryIcon fontSize="small" color="success" />,
    'battery_issue': <BatteryIcon fontSize="small" color="warning" />,
    'charging_issue': <BatteryIcon fontSize="small" color="error" />,
    'wifi_issue': <WifiIcon fontSize="small" color="info" />,
    'speaker_issue': <SpeakerIcon fontSize="small" color="secondary" />,
    'fan_issue': <BuildIcon fontSize="small" color="action" />,
    'overheating': <WhatshotIcon fontSize="small" color="warning" />,
    'software_issue': <BugReportIcon fontSize="small" color="success" />,
    'hardware_issue': <BuildIcon fontSize="small" color="disabled" />,
    'physical_damage': <WarningIcon fontSize="small" color="error" />,
    'other': <WarningIcon fontSize="small" color="action" />
  };
  return iconMap[issueType] || <WarningIcon fontSize="small" />;
};

  // Get issue type label
  const getIssueTypeLabel = (issueType) => {
    const labelMap = {
      'keyboard_issue': 'Keyboard Issue',
      'motherboard_issue': 'Motherboard Issue',
      'screen_issue': 'Screen Issue',
      'battery_issue': 'Battery Issue',
      'charging_issue': 'Charging Issue',
      'wifi_issue': 'WiFi Issue',
      'speaker_issue': 'Speaker Issue',
      'fan_issue': 'Fan Issue',
      'overheating': 'Overheating',
      'software_issue': 'Software Issue',
      'hardware_issue': 'Hardware Issue',
      'physical_damage': 'Physical Damage',
      'other': 'Other Issue'
    };
    return labelMap[issueType] || issueType;
  };

  // Get status chip
  const getStatusChip = (status) => {
    const statusMap = {
      'pending': { color: 'warning', icon: <PendingIcon fontSize="small" />, label: 'Pending' },
      'in_progress': { color: 'info', icon: <UpdateIcon fontSize="small" />, label: 'In Progress' },
      'resolved': { color: 'success', icon: <CheckCircleIcon fontSize="small" />, label: 'Resolved' },
      'cannot_fix': { color: 'error', icon: <WarningIcon fontSize="small" />, label: 'Cannot Fix' }
    };
    const s = statusMap[status] || statusMap.pending;
    return (
      <Chip
        icon={s.icon}
        label={s.label}
        size="small"
        color={s.color}
        sx={{ height: 20, fontSize: '0.7rem' }}
      />
    );
  };

  const filteredProducts = products.filter(product => {
    // Apply search filter (name, code, category, location, barcode, serial)
    if (searchQuery) {
      const q = searchQuery.toLowerCase().trim();
      const matchesSerial = Array.isArray(product.serials) && product.serials.some((s) =>
        String(s.serial_number || '').toLowerCase().includes(q)
      );
      const matchesText =
        String(product.name || '').toLowerCase().includes(q) ||
        String(product.product_code || '').toLowerCase().includes(q) ||
        String(product.category || '').toLowerCase().includes(q) ||
        String(product.barcode || '').toLowerCase().includes(q) ||
        (product.location && String(product.location).toLowerCase().includes(q)) ||
        matchesSerial;
      if (!matchesText) return false;
    }
    
    // Apply category filter
    if (categoryFilter !== 'all' && product.category !== categoryFilter) {
      return false;
    }
    
    // Apply status filter
    if (statusFilter === 'active' && !product.is_active) {
      return false;
    }
    if (statusFilter === 'inactive' && product.is_active) {
      return false;
    }
    if (statusFilter === 'has_issues' && (product.issue_quantity || 0) === 0) {
      return false;
    }
    if (statusFilter === 'no_issues' && (product.issue_quantity || 0) > 0) {
      return false;
    }
    
    // Apply location filter
    if (locationFilter === 'has_location' && (!product.location || product.location.trim() === '')) {
      return false;
    }
    if (locationFilter === 'no_location' && product.location && product.location.trim() !== '') {
      return false;
    }
    if (locationFilter !== 'all' && 
        locationFilter !== 'has_location' && 
        locationFilter !== 'no_location' &&
        locationFilter !== 'has_issues' &&
        locationFilter !== 'no_issues') {
      if (!product.location || product.location !== locationFilter) {
        return false;
      }
    }
    
    return true;
  });

  const getLowStockCount = () => {
    return filteredProducts.filter(p => p.is_low_stock).length;
  };

  const getActiveCount = () => {
    return filteredProducts.filter(p => p.is_active).length;
  };

  const getProductsWithIssuesCount = () => {
    return filteredProducts.filter(p => (p.issue_quantity || 0) > 0).length;
  };

  // Matches the Issues column — sum of issue_quantity per product
  const getTotalIssueItemsCount = () => {
    return products.reduce((sum, p) => sum + (parseInt(p.issue_quantity, 10) || 0), 0);
  };

  // Get active filter count
  const getActiveFilterCount = () => {
    let count = 0;
    if (categoryFilter !== 'all') count++;
    if (statusFilter !== 'all') count++;
    if (locationFilter !== 'all') count++;
    return count;
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchProducts}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(135deg, #f8fafc 0%, #eef7f0 100%)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ color: '#1b4332', fontWeight: 700 }}>
            Products
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {filteredProducts.length} products · {getActiveCount()} active · {getLowStockCount()} low stock · {getProductsWithIssuesCount()} with issues
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Search filters the list instantly — no page reload
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<GrnIcon />}
            onClick={() => navigate('/grn')}
            size="small"
          >
            GRN
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchProducts}
            size="small"
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<IssueIcon />}
            onClick={handleViewAllIssues}
            size="small"
          >
            View All Issues
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<IssueIcon />}
            onClick={handleExportIssues}
            size="small"
          >
            Export Issues
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ExportIcon />}
            onClick={handleExportCSV}
            disabled={filteredProducts.length === 0}
            size="small"
          >
            Export CSV
          </Button>
          {canEditProducts() && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: '#3498db',
              '&:hover': { backgroundColor: '#2980b9' }
            }}
            onClick={handleAddProduct}
          >
            Add Product
          </Button>
          )}
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total Products
                  </Typography>
                  <Typography variant="h4">{filteredProducts.length}</Typography>
                </Box>
                <CategoryIcon sx={{ fontSize: 40, color: '#3498db', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Active Products
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {getActiveCount()}
                  </Typography>
                </Box>
                <InventoryIcon sx={{ fontSize: 40, color: '#2ecc71', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Products with Issues
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {getProductsWithIssuesCount()}
                  </Typography>
                </Box>
                <IssueIcon sx={{ fontSize: 40, color: '#f39c12', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total Issues
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {getTotalIssueItemsCount()}
                  </Typography>
                </Box>
                <WarningIcon sx={{ fontSize: 40, color: '#e74c3c', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter Bar */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          placeholder="Search by name, code, category, location, or serial number..."
          variant="outlined"
          size="small"
          fullWidth
          value={searchQuery}
          onChange={handleSearch}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
        <Button
          variant="outlined"
          startIcon={<FilterIcon />}
          onClick={handleFilterClick}
          sx={{ minWidth: 120 }}
          color={getActiveFilterCount() > 0 ? "primary" : "inherit"}
        >
          Filter {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
        </Button>
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={handleFilterClose}
          PaperProps={{
            sx: {
              maxHeight: 400,
              width: 280,
            },
          }}
        >
          {/* Category Filter Section */}
          <MenuItem disabled sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            <CategoryIcon sx={{ mr: 1, fontSize: 18 }} />
            Category
          </MenuItem>
          <MenuItem 
            selected={categoryFilter === 'all'}
            onClick={() => handleCategoryFilterChange('all')}
            sx={{ pl: 4 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {categoryFilter === 'all' && <CheckIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>All Categories</ListItemText>
            <Chip label={products.length} size="small" sx={{ height: 20, fontSize: '0.75rem' }} />
          </MenuItem>
          {categories.map(category => (
            <MenuItem 
              key={category}
              selected={categoryFilter === category}
              onClick={() => handleCategoryFilterChange(category)}
              sx={{ pl: 4 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {categoryFilter === category && <CheckIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText>{category}</ListItemText>
              <Chip 
                label={products.filter(p => p.category === category).length}
                size="small" 
                sx={{ height: 20, fontSize: '0.75rem' }} 
              />
            </MenuItem>
          ))}
          
          <Divider sx={{ my: 1 }} />
          
          {/* Status Filter Section */}
          <MenuItem disabled sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            <InventoryIcon sx={{ mr: 1, fontSize: 18 }} />
            Status
          </MenuItem>
          <MenuItem 
            selected={statusFilter === 'all'}
            onClick={() => handleStatusFilterChange('all')}
            sx={{ pl: 4 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {statusFilter === 'all' && <CheckIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>All Status</ListItemText>
          </MenuItem>
          <MenuItem 
            selected={statusFilter === 'active'}
            onClick={() => handleStatusFilterChange('active')}
            sx={{ pl: 4 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {statusFilter === 'active' && <CheckIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>Active Only</ListItemText>
            <Chip 
              label={products.filter(p => p.is_active).length}
              size="small" 
              color="success"
              sx={{ height: 20, fontSize: '0.75rem' }} 
            />
          </MenuItem>
          <MenuItem 
            selected={statusFilter === 'inactive'}
            onClick={() => handleStatusFilterChange('inactive')}
            sx={{ pl: 4 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {statusFilter === 'inactive' && <CheckIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>Inactive Only</ListItemText>
            <Chip 
              label={products.filter(p => !p.is_active).length}
              size="small" 
              color="error"
              sx={{ height: 20, fontSize: '0.75rem' }} 
            />
          </MenuItem>
          <MenuItem 
            selected={statusFilter === 'has_issues'}
            onClick={() => handleStatusFilterChange('has_issues')}
            sx={{ pl: 4 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {statusFilter === 'has_issues' && <CheckIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>With Issues</ListItemText>
            <Chip 
              label={products.filter(p => (p.issue_quantity || 0) > 0).length}
              size="small" 
              color="warning"
              sx={{ height: 20, fontSize: '0.75rem' }} 
            />
          </MenuItem>
          <MenuItem 
            selected={statusFilter === 'no_issues'}
            onClick={() => handleStatusFilterChange('no_issues')}
            sx={{ pl: 4 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {statusFilter === 'no_issues' && <CheckIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>No Issues</ListItemText>
            <Chip 
              label={products.filter(p => (p.issue_quantity || 0) === 0).length}
              size="small" 
              color="success"
              sx={{ height: 20, fontSize: '0.75rem' }} 
            />
          </MenuItem>
          
          <Divider sx={{ my: 1 }} />
          
          {/* Location Filter Section */}
          <MenuItem disabled sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            <LocationIcon sx={{ mr: 1, fontSize: 18 }} />
            Location
          </MenuItem>
          <MenuItem 
            selected={locationFilter === 'all'}
            onClick={() => handleLocationFilterChange('all')}
            sx={{ pl: 4 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {locationFilter === 'all' && <CheckIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>All Locations</ListItemText>
          </MenuItem>
          <MenuItem 
            selected={locationFilter === 'has_location'}
            onClick={() => handleLocationFilterChange('has_location')}
            sx={{ pl: 4 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {locationFilter === 'has_location' && <CheckIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>With Location</ListItemText>
            <Chip 
              label={products.filter(p => p.location && p.location.trim() !== '').length}
              size="small" 
              sx={{ height: 20, fontSize: '0.75rem' }} 
            />
          </MenuItem>
          <MenuItem 
            selected={locationFilter === 'no_location'}
            onClick={() => handleLocationFilterChange('no_location')}
            sx={{ pl: 4 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {locationFilter === 'no_location' && <CheckIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>No Location</ListItemText>
            <Chip 
              label={products.filter(p => !p.location || p.location.trim() === '').length}
              size="small" 
              sx={{ height: 20, fontSize: '0.75rem' }} 
            />
          </MenuItem>
          
          {/* Specific Locations */}
          {locations.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <MenuItem disabled sx={{ pl: 4, color: 'text.secondary', fontSize: '0.875rem' }}>
                Specific Locations:
              </MenuItem>
              {locations.map(location => (
                <MenuItem 
                  key={location}
                  selected={locationFilter === location}
                  onClick={() => handleLocationFilterChange(location)}
                  sx={{ pl: 6 }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {locationFilter === location && <CheckIcon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText 
                    primary={location}
                    primaryTypographyProps={{
                      sx: { 
                        fontSize: '0.875rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 150
                      }
                    }}
                  />
                  <Chip 
                    label={products.filter(p => p.location === location).length}
                    size="small"
                    sx={{ 
                      height: 20,
                      fontSize: '0.7rem',
                      ml: 1
                    }}
                  />
                </MenuItem>
              ))}
            </>
          )}
          
          {/* Clear All Filters */}
          <Divider sx={{ my: 1 }} />
          <MenuItem 
            onClick={handleClearFilters}
            sx={{ 
              justifyContent: 'center',
              color: 'primary.main',
              fontWeight: 'medium',
              backgroundColor: 'action.hover'
            }}
          >
            Clear All Filters
          </MenuItem>
        </Menu>
      </Paper>

      {/* Active Filters Display */}
      {(categoryFilter !== 'all' || statusFilter !== 'all' || locationFilter !== 'all') && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f8f9fa' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Active Filters:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {categoryFilter !== 'all' && (
              <Chip 
                label={`Category: ${categoryFilter}`}
                onDelete={() => setCategoryFilter('all')}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
            {statusFilter !== 'all' && (
              <Chip 
                label={`Status: ${
                  statusFilter === 'active' ? 'Active' : 
                  statusFilter === 'inactive' ? 'Inactive' :
                  statusFilter === 'has_issues' ? 'With Issues' : 'No Issues'
                }`}
                onDelete={() => setStatusFilter('all')}
                color={statusFilter === 'active' ? 'success' : 
                       statusFilter === 'inactive' ? 'error' :
                       statusFilter === 'has_issues' ? 'warning' : 'info'}
                variant="outlined"
                size="small"
              />
            )}
            {locationFilter !== 'all' && (
              <Chip 
                label={`Location: ${
                  locationFilter === 'has_location' ? 'With Location' : 
                  locationFilter === 'no_location' ? 'No Location' : 
                  locationFilter
                }`}
                onDelete={() => setLocationFilter('all')}
                color="secondary"
                variant="outlined"
                size="small"
                icon={<LocationIcon fontSize="small" />}
              />
            )}
          </Box>
        </Paper>
      )}

      {/* Products Table */}
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong></strong></TableCell>
              <TableCell><strong>Code</strong></TableCell>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Category</strong></TableCell>
              <TableCell><strong>Location</strong></TableCell>
              <TableCell><strong>Price</strong></TableCell>
              <TableCell><strong>Total Qty</strong></TableCell>
              <TableCell><strong>Good Stock</strong></TableCell>
              <TableCell><strong>Issues</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No products found. Try adjusting your search or filters.
                  </Typography>
                  <Button 
                    variant="outlined" 
                    startIcon={<AddIcon />}
                    onClick={handleAddProduct}
                    sx={{ mt: 2 }}
                  >
                    Add New Product
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              filteredProducts.map((product) => {
                const totalQty = product.quantity || 0;
                const goodQty = product.good_quantity || 0;
                const issueQty = product.issue_quantity || 0;
                const hasIssues = issueQty > 0;
                const productIssuesList = productIssues[product.id] || [];
                const isLoading = loadingIssues[product.id];
                
                // ============ NEW: Validate quantities ============
                const calculatedTotal = goodQty + issueQty;
                const hasDiscrepancy = calculatedTotal !== totalQty;
                
                // If there's a discrepancy, adjust good quantity for display
                const displayGoodQty = hasDiscrepancy ? totalQty - issueQty : goodQty;
                const displayTotalQty = totalQty;
                const displayIssueQty = issueQty;
                // =================================================

                return (
                  <React.Fragment key={product.id}>
                    <TableRow 
                      hover
                      sx={{ 
                        '&:hover': { backgroundColor: '#f9f9f9' },
                        opacity: product.is_active ? 1 : 0.7,
                        backgroundColor: hasIssues ? '#fff3e0' : 'inherit'
                      }}
                    >
                      <TableCell>
                        {hasIssues && (
                          <IconButton
                            size="small"
                            onClick={() => handleExpandProduct(product.id)}
                          >
                            {expandedProduct === product.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {product.product_code}
                          </Typography>
                          {product.barcode && (
                            <Typography variant="caption" color="text.secondary">
                              {product.barcode}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2">{product.name}</Typography>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            component="div"
                            sx={{ 
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {product.description || 'No description'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={product.category} 
                          size="small"
                          sx={{ 
                            backgroundColor: getCategoryColor(product.category),
                            color: getCategoryTextColor(product.category),
                            fontWeight: 'medium',
                            border: `1px solid ${getCategoryTextColor(product.category)}20`
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {product.location ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationIcon sx={{ fontSize: 16, color: '#7f8c8d' }} />
                            <Typography variant="body2">
                              {product.location}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Not specified
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                          {product.price_lkr || `LKR ${product.price}`}
                        </Typography>
                        {product.cost_price > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            Cost: {product.cost_price_lkr || `LKR ${product.cost_price}`}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <Chip
                            label={displayTotalQty}
                            size="small"
                            sx={{ 
                              bgcolor: hasDiscrepancy ? '#fff3e0' : '#f5f5f5',
                              color: hasDiscrepancy ? '#f57c00' : 'inherit',
                              fontWeight: 'bold',
                              minWidth: 50
                            }}
                          />
                          {hasDiscrepancy && (
                            <Tooltip title={`Data discrepancy: Good (${goodQty}) + Issue (${issueQty}) = ${calculatedTotal}, but Total is ${totalQty}`}>
                              <WarningIcon color="warning" fontSize="small" />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={displayGoodQty}
                          size="small"
                          sx={{ 
                            bgcolor: hasDiscrepancy ? '#fff3e0' : '#e8f5e8',
                            color: hasDiscrepancy ? '#f57c00' : '#2e7d32',
                            fontWeight: 'bold',
                            minWidth: 50
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Badge 
                          badgeContent={displayIssueQty} 
                          color="error"
                          overlap="circular"
                        >
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleViewIssues(product)}
                            disabled={!hasIssues}
                          >
                            <IssueIcon />
                          </IconButton>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            label={product.is_active ? 'Active' : 'Inactive'} 
                            color={product.is_active ? 'success' : 'error'}
                            size="small"
                            variant={product.is_active ? 'filled' : 'outlined'}
                          />
                          {product.is_low_stock && (
                            <Tooltip title="Low Stock">
                              <WarningIcon color="warning" fontSize="small" />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          <Tooltip title="View GRN History">
                            <IconButton
                              size="small"
                              onClick={() => handleViewGrnHistory(product)}
                              sx={{ color: '#27ae60' }}
                            >
                              <GrnIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Issues">
                            <span>
                              <IconButton 
                                size="small" 
                                onClick={() => handleViewIssues(product)}
                                disabled={!hasIssues}
                                sx={{ color: hasIssues ? '#f39c12' : '#bdc3c7' }}
                              >
                                <IssueIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small" 
                              onClick={() => handleView(product)}
                              sx={{ color: '#3498db' }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canEditProducts() && (
                          <Tooltip title="Edit Product">
                            <IconButton 
                              size="small"
                              sx={{ color: '#f39c12' }}
                              onClick={() => handleEditProduct(product.id)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          )}
                          {canDeleteProducts() && (
                          <Tooltip title="Delete Product">
                            <IconButton 
                              size="small" 
                              onClick={() => handleDelete(product.id)}
                              sx={{ color: '#e74c3c' }}
                              disabled={!product.is_active}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    
                    {/* Expanded Issues Row with Serial Numbers */}
                    {expandedProduct === product.id && (
                      <TableRow>
                        <TableCell colSpan={11} sx={{ p: 0, borderBottom: 'none' }}>
                          <Collapse in={expandedProduct === product.id}>
                            <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <IssueIcon color="warning" />
                                  Issues for {product.name}
                                  <Chip 
                                    label={`${productIssuesList.length} of ${issueQty} issues`} 
                                    size="small"
                                    color="warning"
                                  />
                                </Typography>
                                <Button
                                  size="small"
                                  startIcon={<RefreshIcon />}
                                  onClick={() => refreshProductIssues(product.id)}
                                  disabled={isLoading}
                                >
                                  Refresh
                                </Button>
                              </Box>
                              
                              {isLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                  <CircularProgress size={30} />
                                </Box>
                              ) : productIssuesList.length > 0 ? (
                                <TableContainer component={Paper} variant="outlined">
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
                                        <TableCell>Serial Number</TableCell>
                                        <TableCell>Issue Type</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell>Severity</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Reported By</TableCell>
                                        <TableCell>Reported Date</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {productIssuesList.map((issue) => (
                                        <TableRow key={issue.id} hover>
                                          <TableCell>
                                            <Chip
                                              icon={<SerialIcon fontSize="small" />}
                                              label={issue.serial_number}
                                              size="small"
                                              variant="outlined"
                                              sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                                            />
                                          </TableCell>
                                          <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              {getIssueTypeIcon(issue.issue_type)}
                                              <Typography variant="body2">
                                                {getIssueTypeLabel(issue.issue_type)}
                                              </Typography>
                                            </Box>
                                          </TableCell>
                                          <TableCell>
                                            <Tooltip title={issue.issue_description}>
                                              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                {issue.issue_description}
                                              </Typography>
                                            </Tooltip>
                                          </TableCell>
                                          <TableCell>
                                            <Chip
                                              label={issue.severity}
                                              size="small"
                                              color={
                                                issue.severity === 'critical' ? 'error' :
                                                issue.severity === 'high' ? 'warning' :
                                                issue.severity === 'medium' ? 'info' : 'success'
                                              }
                                              sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                          </TableCell>
                                          <TableCell>
                                            {getStatusChip(issue.status)}
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2">
                                              {issue.reported_by}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2">
                                              {new Date(issue.reported_date).toLocaleDateString()}
                                            </Typography>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              ) : (
                                <Alert severity="info" sx={{ m: 2 }}>
                                  No issue details found for this product. The issue count ({issueQty}) may need to be updated.
                                </Alert>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Quick Add FAB */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          backgroundColor: '#3498db',
          '&:hover': { backgroundColor: '#2980b9' }
        }}
        onClick={handleAddProduct}
      >
        <AddIcon />
      </Fab>

      {/* Product Detail Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        {selectedProduct && (
          <>
            <DialogTitle>
              <Typography variant="h6">
                Product Details: {selectedProduct.name}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {selectedProduct.product_code}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Product Code"
                    value={selectedProduct.product_code}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Category"
                    value={selectedProduct.category}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Price"
                    value={selectedProduct.price_lkr || `LKR ${selectedProduct.price}`}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Cost Price"
                    value={selectedProduct.cost_price_lkr || `LKR ${selectedProduct.cost_price || '0.00'}`}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Total Stock"
                    value={`${selectedProduct.quantity} ${selectedProduct.unit}`}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Good Stock"
                    value={selectedProduct.good_quantity || 0}
                    slotProps={{
                      input: {
                        readOnly: true,
                        sx: { color: '#2e7d32', fontWeight: 'bold' }
                      },
                    }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Issue Stock"
                    value={selectedProduct.issue_quantity || 0}
                    slotProps={{
                      input: {
                        readOnly: true,
                        sx: { color: '#c62828', fontWeight: 'bold' }
                      },
                    }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={selectedProduct.description || 'No description'}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                    margin="normal"
                    multiline
                    rows={3}
                  />
                </Grid>
                {selectedProduct.barcode && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Barcode"
                      value={selectedProduct.barcode}
                      slotProps={{
                        input: {
                          readOnly: true,
                        },
                      }}
                      margin="normal"
                    />
                  </Grid>
                )}
                {selectedProduct.supplier && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Supplier"
                      value={selectedProduct.supplier}
                      slotProps={{
                        input: {
                          readOnly: true,
                        },
                      }}
                      margin="normal"
                    />
                  </Grid>
                )}
                {selectedProduct.location && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Location"
                      value={selectedProduct.location}
                      slotProps={{
                        input: {
                          readOnly: true,
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationIcon />
                            </InputAdornment>
                          ),
                        },
                      }}
                      margin="normal"
                    />
                  </Grid>
                )}
                {selectedProduct.has_warranty && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Warranty"
                      value={`${selectedProduct.warranty_months} months (${selectedProduct.warranty_type})`}
                      slotProps={{
                        input: {
                          readOnly: true,
                        },
                      }}
                      margin="normal"
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Total Value"
                    value={selectedProduct.total_value || `LKR ${calculateInventoryValue(selectedProduct).toFixed(2)}`}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Status"
                    value={selectedProduct.is_active ? 'Active' : 'Inactive'}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button 
                onClick={() => setOpenDialog(false)} 
                color="primary"
                variant="outlined"
              >
                Close
              </Button>
              {canEditProducts() && (
              <Button 
                onClick={() => {
                  setOpenDialog(false);
                  handleEditProduct(selectedProduct.id);
                }}
                color="primary"
                variant="contained"
              >
                Edit Product
              </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Issues Detail Dialog with Serial Numbers */}
      <Dialog open={issuesDialogOpen} onClose={() => setIssuesDialogOpen(false)} maxWidth="lg" fullWidth>
        {selectedProductForIssues && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IssueIcon color="warning" />
                  <Typography variant="h6">
                    Issues: {selectedProductForIssues.name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => refreshProductIssues(selectedProductForIssues.id)}
                  >
                    Refresh
                  </Button>
                  <Chip 
                    label={`${selectedProductForIssues.issue_quantity || 0} Issues`}
                    color="warning"
                  />
                </Box>
              </Box>
              <Typography variant="subtitle2" color="text.secondary">
                Code: {selectedProductForIssues.product_code} | Category: {selectedProductForIssues.category}
              </Typography>
            </DialogTitle>
            <DialogContent>
              {loadingIssues[selectedProductForIssues.id] ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : productIssues[selectedProductForIssues.id]?.length > 0 ? (
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                  <Table size="medium">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell>Serial Number</TableCell>
                        <TableCell>Issue Type</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Severity</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Reported By</TableCell>
                        <TableCell>Reported Date</TableCell>
                        <TableCell>Repair Cost</TableCell>
                        <TableCell>Notes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productIssues[selectedProductForIssues.id].map((issue) => (
                        <TableRow key={issue.id} hover>
                          <TableCell>
                            <Chip
                              icon={<SerialIcon fontSize="small" />}
                              label={issue.serial_number}
                              size="small"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getIssueTypeIcon(issue.issue_type)}
                              <Typography variant="body2">
                                {getIssueTypeLabel(issue.issue_type)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={issue.issue_description}>
                              <Typography variant="body2" sx={{ maxWidth: 200 }}>
                                {issue.issue_description}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={issue.severity}
                              size="small"
                              color={
                                issue.severity === 'critical' ? 'error' :
                                issue.severity === 'high' ? 'warning' :
                                issue.severity === 'medium' ? 'info' : 'success'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {getStatusChip(issue.status)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {issue.reported_by}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(issue.reported_date).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {issue.repair_cost ? `LKR ${issue.repair_cost}` : '-'}
                          </TableCell>
                          <TableCell>
                            <Tooltip title={issue.notes || 'No notes'}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                {issue.notes || '-'}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary" gutterBottom>
                    No issue details found for this product.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    The product has {selectedProductForIssues.issue_quantity || 0} reported issues but no details are available.
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIssuesDialogOpen(false)} variant="contained">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* All Issues Dialog */}
      <Dialog open={allIssuesDialogOpen} onClose={() => setAllIssuesDialogOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IssueIcon color="warning" />
              <Typography variant="h6">All Product Issues</Typography>
              <Chip label={`${filteredAllIssues.length} issues`} color="warning" size="small" />
            </Box>
            <Button size="small" startIcon={<RefreshIcon />} onClick={fetchAllIssuesList}>
              Refresh
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search serial, product, type, description..."
              value={allIssuesSearch}
              onChange={(e) => setAllIssuesSearch(e.target.value)}
              sx={{ minWidth: 280, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={allIssuesStatusFilter}
                label="Status"
                onChange={(e) => setAllIssuesStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="cannot_fix">Cannot Fix</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {loadingAllIssues ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredAllIssues.length === 0 ? (
            <Alert severity="info">No issues found.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>Product</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Serial Number</TableCell>
                    <TableCell>Issue Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Reported By</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAllIssues.map((issue) => (
                    <TableRow key={issue.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {issue.product?.name || products.find(p => p.id === issue.product_id)?.name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={issue.product?.product_code || '-'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<SerialIcon fontSize="small" />}
                          label={issue.serial_number}
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ fontFamily: 'monospace' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {getIssueTypeIcon(issue.issue_type)}
                          <Typography variant="body2">{getIssueTypeLabel(issue.issue_type)}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={issue.issue_description || ''}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {issue.issue_description || 'No description'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={issue.severity}
                          size="small"
                          color={
                            issue.severity === 'critical' ? 'error' :
                            issue.severity === 'high' ? 'warning' :
                            issue.severity === 'medium' ? 'info' : 'success'
                          }
                        />
                      </TableCell>
                      <TableCell>{getStatusChip(issue.status)}</TableCell>
                      <TableCell>{issue.reported_by || 'System'}</TableCell>
                      <TableCell>
                        {issue.reported_date ? new Date(issue.reported_date).toLocaleDateString() : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAllIssuesDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <GRNProductHistory
        open={grnHistoryOpen}
        onClose={() => { setGrnHistoryOpen(false); setSelectedProductForGrn(null); }}
        product={selectedProductForGrn}
      />
    </Box>
  );
};

export default Products;